import { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CustomTable, { IColumn } from "../../common/CustomTable/CustomTable";
import {
  getSecretKeys,
  getDecryptedSecretKey,
  updateDecryptedSecretKey,
} from "./secretKeysActions";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  ISecretKeys,
  IUpdateSecretKeyRequestBody,
} from "../../models/admin/secretKeys/secretKeys";
import { RootState } from "../../store/store";

const SecretKeys = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:767px)");
  const { secretKeys, decryptedKey } = useAppSelector(
    (state) => state.secretKeys
  );
  const { calledReducerType, isLoading } = useAppSelector(
    (store: RootState) => store.common
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [editKey, setEditKey] = useState<ISecretKeys | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    key: "",
    value: "",
  });
  const [isValueEdited, setIsValueEdited] = useState(false);

  useEffect(() => {
    dispatch(getSecretKeys());
  }, [dispatch]);

  const handleEditKey = (id: string) => {
    const keyToEdit = secretKeys?.find((key) => key?.id === id);
    if (keyToEdit) {
      setEditKey(keyToEdit);
      setOpenDialog(true);
      setErrors({ key: "", value: "" });
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditKey(null);
    setShowPassword(false);
    setIsValueEdited(false);
  };

  const handleSave = async () => {
    let newErrors = {
      key: "",
      value: "",
    };

    // Validate fields
    if (!editKey?.key) newErrors.key = "Key is required & cannot be empty";
    if (!editKey?.value)
      newErrors.value = "Value is required & cannot be empty";

    // Check if there are any errors
    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);
      return;
    }

    if (editKey) {
      const { id, key, value } = editKey;
      const updateKeyRequestBody: IUpdateSecretKeyRequestBody = {
        id,
        key,
        ...(isValueEdited && { value }),
      };
      await dispatch(
        updateDecryptedSecretKey({
          id,
          requestBody: updateKeyRequestBody,
        })
      );
      dispatch(getSecretKeys());
    }
    handleDialogClose();
  };

  const handleViewDecryptedKey = async () => {
    if (!showPassword) {
      if (editKey?.id) {
        await dispatch(getDecryptedSecretKey(editKey.id));
        setShowPassword(true);
      }
    } else {
      setShowPassword(false);
    }
  };

  useEffect(() => {
    if (decryptedKey) {
      setEditKey((prev) => ({
        ...prev!,
        value: decryptedKey.value || "",
      }));
    }
  }, [dispatch, decryptedKey]);

  const columns: IColumn[] = [
    {
      label: "Name",
      accessorKey: "key",
    },
    {
      label: "Encrypted API key ",
      styles: {
        maxWidth: "40vw",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      },
      accessorKey: "value",
    },
    {
      label: "Actions",
      styles: { textAlign: "center" },
      Cell: (item: any): JSX.Element => {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <IconButton 
              size="small" 
              onClick={() => handleEditKey(item.id)}
              id={`secret_keys_edit_${item.id}`}
              data-test-id={`secret_keys_edit_${item.id}`}
              aria-label={`Edit ${item.key} Secret Key`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      sx={{ paddingY: 3, maxWidth: isMobile ? "calc(100vw - 48px)" : "none" }}
      id="secret_keys_container"
      data-test-id="secret_keys_container"
      aria-label="Secret Keys Management"
    >
      {secretKeys && secretKeys.length > 0 ? (
        <CustomTable
          columns={columns}
          data={secretKeys}
          pagination
          stickyHeader
          displaySkeleton={
            isLoading && calledReducerType === "secretsKeys/getSecretsKeys"
          }
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
          }}
          id="secret_keys_empty"
          data-test-id="secret_keys_empty"
          aria-label="No Secret Keys Available"
        >
          <Typography variant="h6" id="secret_keys_empty_title" data-test-id="secret_keys_empty_title" aria-label="No Secret Keys Available">No secret keys available.</Typography>
        </Box>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleDialogClose}
        id="secret_keys_dialog"
        data-test-id="secret_keys_dialog"
        aria-label="Update Secret Key Dialog"
      >
        <DialogTitle id="secret_keys_dialog_title" data-test-id="secret_keys_dialog_title" aria-label="Update Secret Key">Update Secret Key</DialogTitle>
        <DialogContent
          sx={{
            paddingTop: 4,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "center",
            gap: 2,
            minWidth: 400,
            "&.MuiDialogContent-root": {
              paddingTop: "16px !important",
            },
          }}
          id="secret_keys_dialog_content"
          data-test-id="secret_keys_dialog_content"
          aria-label="Secret Key Form"
        >
          <TextField
            label="Name"
            value={editKey?.key || ""}
            onChange={(e) => {
              const trimmedValue = e.target.value.trim();
              setEditKey({ ...editKey!, key: trimmedValue });
              setErrors({
                ...errors,
                key: trimmedValue ? "" : "Key cannot be empty",
              });
            }}
            fullWidth
            error={!!errors.key}
            helperText={errors.key}
            id="secret_keys_name_input"
            data-test-id="secret_keys_name_input"
            aria-label="Secret Key Name"
          />
          <TextField
            label="Decrypted API Key"
            type={showPassword ? "text" : "password"}
            value={editKey?.value}
            onChange={(e) => {
              const trimmedValue = e.target.value.trim();
              setEditKey({ ...editKey!, value: trimmedValue });
              setIsValueEdited(true);
              setErrors({
                ...errors,
                value: trimmedValue ? "" : "Value cannot be empty",
              });
            }}
            fullWidth
            error={!!errors.value}
            helperText={errors.value}
            InputProps={{
              endAdornment: (
                <IconButton 
                  onClick={() => handleViewDecryptedKey()}
                  id="secret_keys_toggle_visibility"
                  data-test-id="secret_keys_toggle_visibility"
                  aria-label={showPassword ? "Hide Secret Key" : "Show Secret Key"}
                >
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              ),
            }}
            id="secret_keys_value_input"
            data-test-id="secret_keys_value_input"
            aria-label="Secret Key Value"
          />
        </DialogContent>
        <DialogActions id="secret_keys_dialog_actions" data-test-id="secret_keys_dialog_actions" aria-label="Dialog Actions">
          <Button 
            onClick={handleDialogClose} 
            sx={{ borderRadius: "4px" }}
            id="secret_keys_cancel_button"
            data-test-id="secret_keys_cancel_button"
            aria-label="Cancel Update"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleSave()} 
            sx={{ borderRadius: "4px" }}
            id="secret_keys_save_button"
            data-test-id="secret_keys_save_button"
            aria-label="Save Changes"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecretKeys;
